<script setup lang="ts">
/**
 * The Explore tile: the dark card that opens the middle row (IPD-2978 12.2).
 *
 * It was the only tile of the bento with no state at all, and the payload carried nothing for it
 * (BE spec §3). It has two faces now, and two dates decide between them — which of them applies is
 * the page's question, answered before this is rendered. Whether the tile is shown at all is a
 * per-user question too: a licence can blacklist Explore.
 *
 * Two layouts (IPD-3234): the copy alone, and the same copy beside a fanned ring of content covers
 * that turns once a minute. The page decides both — it hands the tile the whole discover row for a
 * face that has a ring, and the two content tiles fall below — and passes the face here.
 *
 * The ring belongs to the wide card. A narrower one draws the copy alone, which is what the design
 * asks for and what keeps the words off the covers (IPD-3270).
 */
import {computed, ref, watch} from 'vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {pagesPath} from '@/ts/router/pages-path';
import exploreBackdrop from '@/assets/img/today-explore-backdrop.webp';
import ringTile1 from '@/assets/img/today-explore-ring-1.webp';
import ringTile2 from '@/assets/img/today-explore-ring-2.webp';
import ringTile3 from '@/assets/img/today-explore-ring-3.webp';
import ringTile4 from '@/assets/img/today-explore-ring-4.webp';
import ringTile5 from '@/assets/img/today-explore-ring-5.webp';
import ringTile6 from '@/assets/img/today-explore-ring-6.webp';
import ringTile7 from '@/assets/img/today-explore-ring-7.webp';
import ringTile8 from '@/assets/img/today-explore-ring-8.webp';
import ringTile9 from '@/assets/img/today-explore-ring-9.webp';
import ringTileNew from '@/assets/img/today-explore-ring-new.webp';

/**
 * Nine covers, which is the ring the design draws (IPD-3234) — at nine the step is its 40 degrees.
 * The angle is derived from the count rather than written down beside it, so a ring of any length
 * still closes on itself.
 *
 * Generic on purpose for now. They are a prop rather than a constant because the ticket ends with
 * these being the reader's own recommendations — at which point the page passes the real covers and
 * nothing here changes.
 */
const GENERIC_RING_TILES = [
  ringTile1, ringTile2, ringTile3, ringTile4, ringTile5, ringTile6, ringTile7, ringTile8, ringTile9,
];

/**
 * The cover that arrives, drawn from outside the nine on purpose: it appears in the personalised
 * ring and nowhere in the generic one, which is the claim the card's copy makes. Repeating one of
 * the nine would show the reader the same picture twice, side by side on the arc.
 */
const GENERIC_ARRIVING_TILE = ringTileNew;

const props = defineProps<{
  /**
   * The reader has not answered an assessment yet, which is the state the ring belongs to. Asked of
   * the page rather than inferred here: the same page decides, from the same answer, that this tile
   * takes the whole row — and the tile is wide for other reasons too. A licence whose payload
   * carries no content leaves it alone in its row, and that reader has answered plenty.
   */
  firstUse?: boolean;
  /**
   * Explore holds recommendations chosen after the reader's last answer. The card says so, and the
   * one cover the default ring does not carry drops into it.
   */
  hasNewContent?: boolean;
  /** Covers for the ring the wide layout draws, in the order they sit on the circle. */
  ringTiles?: readonly string[];
  /** The cover that arrives, which must not be one of `ringTiles` — it is what is new. */
  arrivingTile?: string;
  /**
   * The covers are still on their way. The card is drawn whole either way — the shape and the face
   * come from the payload — and the ring alone waits, so it is only ever drawn with the pictures it
   * keeps rather than changing them under the reader.
   */
  coversPending?: boolean;
}>();

// Both faces are worth the room the ring needs: one invites a reader with nothing yet, the other
// shows them what arrived.
//
// Only the first draw waits for the pictures. The band is read again when the reader answers, for
// the cover that drops in, and a ring that came down for that would go and come back — which is
// true of the stock ring as much as of one carrying the reader's own covers.
const ringDrawn = ref(false);
const showRing = computed<boolean>(() => (
  Boolean(props.firstUse || props.hasNewContent) && (ringDrawn.value || !props.coversPending)
));
watch(showRing, (drawn) => {
  if (drawn) {
    ringDrawn.value = true;
  }
}, {immediate: true});

/**
 * One picture per place on the circle, the reader's own where their band reaches and the tile's
 * own where it does not (IPD-3270). Never both: drawn one over the other, each place downloads two
 * pictures and shows whichever lands first, which reads as the ring changing its mind.
 *
 * The arriving cover leads, so it lands at the apex — the middle of what the card shows, where the
 * eye already is. It is an ADDITION: the nine behind it are the ones the ring already carried, so
 * the reader sees one join rather than one replace another.
 */
const ringCovers = computed<readonly string[]>(() => {
  const stock = props.hasNewContent ? [GENERIC_ARRIVING_TILE, ...GENERIC_RING_TILES] : GENERIC_RING_TILES;
  const own = props.hasNewContent
    ? [props.arrivingTile, ...props.ringTiles ?? []]
    : props.ringTiles ?? [];

  // `||` rather than `??`: the empty string is a picture nobody can draw, and it is what a cover
  // built from an id the media host cannot resolve comes to.
  return stock.map((picture, index) => own[index] || picture);
});

// Taken from the ring actually drawn rather than from the constant above: the product of the two
// must be 360 whatever the count, or a gap sweeps through the visible arc once the ring turns.
const ringStepAngle = computed<number>(() => 360 / ringCovers.value.length);

/**
 * The shortcut was taken (IPD-2974). Announced although the button is a link and navigates on its
 * own, the way the cards do: a page that only heard about the controls that need it would count a
 * fraction of what it sends to Explore.
 */
const emit = defineEmits<{browse: []}>();
</script>

<template>
  <section class="explore-tile">
    <!-- Decorative: the card says everything it means in the copy below, and the picture is
         there to stop it reading as a plain black rectangle (IPD-3130). -->
    <span
      class="explore-tile__art"
      :style="{backgroundImage: `url(${exploreBackdrop})`}"
      aria-hidden="true"
    />

    <!--
      The ring. Decorative and hidden from assistive tech: the covers are not reachable from here,
      and what the card offers is said in the copy and taken by the CTA.
    -->
    <!--
      Keyed on the face, not just shown by it: answering the first assessment swaps one for the
      other while the page is open, and a patched-in-place ring would keep the phase it had — the
      arriving cover would fall in sideways, since its 110px ride in the spin layer's own rotated
      frame is only "down" while the ring is back at 0deg. Remounting restarts both clocks at once,
      which is the invariant the two-revolution repeat rests on.
    -->
    <span v-if="showRing" :key="hasNewContent ? 'new' : 'generic'" class="explore-tile__ring" aria-hidden="true">
      <!--
        The tiles ride on their own layer, inside the one that carries the scrim: the turn this
        turn pivots on the ring's centre, and spinning a single element would drag the gradient
        round with it.
      -->
      <span class="explore-tile__ring-spin">
        <span
          v-for="(cover, index) in ringCovers"
          :key="index"
          class="explore-tile__ring-tile"
          :class="{'explore-tile__ring-tile--arriving': hasNewContent && index === 0}"
          :style="{'--a': `${index * ringStepAngle}deg`, backgroundImage: `url(${cover})`}"
        />
      </span>
    </span>
    <!-- Opaque at the bottom and fading up, so the tiles rise out of the card's dark ground and
         the lower arc stays buried instead of poking into the corners. -->
    <span v-if="showRing" class="explore-tile__ring-fade" aria-hidden="true"/>

    <div class="explore-tile__intro">
      <span class="explore-tile__eyebrow">{{ $t('today_explore_eyebrow') }}</span>
      <h2 class="explore-tile__heading">
        {{ hasNewContent ? $t('today_explore_new_title') : $t('today_explore_title') }}
      </h2>
      <p class="explore-tile__body">
        {{ hasNewContent ? $t('today_explore_new_body') : $t('today_explore_body') }}
      </p>
    </div>

    <balm-btn
      :theme="hasNewContent ? 'inverse' : 'light-white'"
      paddings="none"
      icon="arrow-narrow-right.svg"
      :to="pagesPath.main.explore"
      :label="hasNewContent ? $t('today_explore_new_cta') : $t('today_explore_cta')"
      class="explore-tile__cta"
      :class="{'explore-tile__cta--new': hasNewContent}"
      @click="emit('browse')"
    />
  </section>
</template>

<style scoped lang="scss">
// Padding, radius and heading voice come from the page tokens, so this tile keeps the same
// rhythm as the ones beside it. The design draws this card at an 18px radius against the 16px of
// the row above; unified to the bento's own radius rather than introducing a third value for one
// card — a one-token change if design wants the 18 back (raised with the other Today notes).
.explore-tile {
  // Named rather than anonymous: an unnamed query binds to the nearest container, which is this
  // element today but would silently become a new one the moment anything between here and the
  // bento declared containment.
  container: explore-tile / inline-size;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: var(--today-tile-padding);
  border-radius: var(--today-radius-section);
  background: var(--today-surface-dark);
  color: var(--today-ink-on-dark);
  overflow: hidden;
}

// The ring belongs to the wide card and nowhere else (IPD-3270). The design does not ask for it on
// a phone — there it competes with the words for a reader trying to read them, beside two
// recommendation cards already carrying pictures of their own — and below a certain width it
// cannot be had anyway: the circle's centre is a percentage of the card, so it swings left into
// the copy as the card narrows, and the copy is already capped at 424.
//
// Measured rather than chosen, over a full turn with the copy's own box sampled pixel by pixel: a
// card of 1032 holds the body line at 8.18:1 and one of 992 drops it to 1.71:1, so the line falls
// between them. A container query measures the CONTENT box, which is the card less its padding,
// and 940 of that is the far side of the step with the margin on the side that matters.
//
// In windows: a laptop at 1080 and up, and no tablet.
.explore-tile__ring,
.explore-tile__ring-fade {
  display: none;
}

@container explore-tile (min-width: 940px) {
  .explore-tile__ring,
  .explore-tile__ring-fade {
    display: block;
  }

  // With the ring drawn, the card's own photograph comes forward: the design's ground is a blurred
  // picture rather than a flat dark. The mask keeps it off the copy column and at full strength
  // out under the covers, which is how the design fades it — its scrim runs at 259.5deg with the
  // opaque end on the left.
  .explore-tile:has(.explore-tile__ring) .explore-tile__art {
    opacity: 0.45;
    mask-image: linear-gradient(to right, transparent 8%, #000 55%);
    -webkit-mask-image: linear-gradient(to right, transparent 8%, #000 55%);
  }

  // Capped so the headline breaks where the design breaks it instead of running the width of the
  // card and colliding with the ring.
  .explore-tile__intro,
  .explore-tile__body {
    max-width: 424px;
  }
}

// Ring geometry, straight from the design system's published values: a circle fitted to the nine
// tile centres of the 1120 frame — (774.2, 434.7) r 268.7, which is 69.1% across. The centre sits
// BELOW the card on purpose, so the tile's own `overflow: hidden` crops everything but the top arc;
// that crop is what produces the fan. Fixed px rather than tokens because the circle is a drawing,
// not spacing: scaling it with the breakpoints would change which part of the arc survives.
.explore-tile__ring {
  // Not `--ease-out`: measured against the design system's own curves, that one spends 65% of the
  // travel in the first 150ms and the move is over before it registers. This one keeps going
  // through the watchable part and settles a few pixels past its place before coming back, and
  // that overshoot is what makes the landing read.
  --explore-arrive-ease: cubic-bezier(0.34, 1.56, 0.64, 1);
  --ring-cx: 69.1%;
  --ring-cy: 435px;
  --ring-radius: 269px;
  --ring-tile-w: 142px;
  --ring-tile-h: 185px;

  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  // The design's "Photos" scrim: a 65% -> 100% ramp on a layer at 0.8 opacity, so it runs 52% ->
  // 80% and never reaches solid — which is what keeps the photograph alive under the tiles.
  background-image: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--today-surface-dark) 52%, transparent) 0%,
    color-mix(in srgb, var(--today-surface-dark) 80%, transparent) 100%
  );
}

// One plain revolution a minute — 6 degrees a second. Slow enough to read as drift rather than as
// motion competing with the copy, and a single compositor-friendly transform, so it costs nothing
// to keep running. The tiles ride on this layer and the scrim stays on the one above: rotating a
// single element would drag the gradient round with them.
.explore-tile__ring-spin {
  position: absolute;
  inset: 0;
  // Must match the ring centre exactly: the tiles orbit the point they were placed around, and any
  // other origin makes the ring wobble instead of turn.
  transform-origin: var(--ring-cx) var(--ring-cy);
  animation: explore-ring-turn 60s linear infinite;
}

// The arrival (IPD-3256): the cover starts a short way above its own place on the circle and
// settles into it, fading as it goes. Its own clock, not the ring's — the ring never pauses for it,
// which is what makes the cover read as joining something already moving rather than as the only
// thing alive on the card. Two whole revolutions fit the repeat, so it always lands at the same
// point of the arc.
//
// The move is 0.92% of the repeat — 1.1s of 120s — and the rest of it is the cover riding round
// with the others. Keyframe offsets cannot be custom properties, so the two are tuned together or
// not at all. It repeats rather than playing once: the arrival is the face's own heartbeat, and a
// reader who looks up two minutes later sees what the card is telling them.
// Held until the card has finished arriving itself — the bento's morph, which paints a snapshot of
// the page over the whole of it, and then the ring's own fade. Played under either the drop is
// simply not seen: it is over in the first 1.1s of the cycle.
.explore-tile__ring-tile--arriving {
  animation: explore-ring-arrive 120s var(--explore-arrive-ease) 620ms infinite both;
}

@keyframes explore-ring-arrive {
  0% {
    opacity: 0;
    transform: translateY(-110px) rotate(var(--a)) translateY(calc(var(--ring-radius) * -1));
  }

  0.92%, 100% {
    opacity: 1;
    transform: rotate(var(--a)) translateY(calc(var(--ring-radius) * -1));
  }
}

@keyframes explore-ring-turn {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Decorative motion: the same content is reachable through the CTA, so it stops outright rather
// than slowing down, and the tiles rest where they were drawn.
.explore-tile__ring-tile {
  position: absolute;
  left: var(--ring-cx);
  top: var(--ring-cy);
  width: var(--ring-tile-w);
  height: var(--ring-tile-h);
  margin-left: calc(var(--ring-tile-w) / -2);
  margin-top: calc(var(--ring-tile-h) / -2);
  border-radius: 14px;
  background-position: center;
  background-size: cover;
  // No counter-rotation: each tile keeps the angle that puts it on the circle, which is what makes
  // the ring read as a fan rather than as a row of upright cards on a curve.
  transform: rotate(var(--a)) translateY(calc(var(--ring-radius) * -1));
  // Three stacked shadows in the design, all offset the same 5px to the left. Raw black alphas
  // rather than the elevation tokens: this is a photographic shadow the tiles carry from the mock,
  // not a surface lifting off the page.
  box-shadow:
    -5px 0 7px rgb(0 0 0 / 10%),
    -5px 28px 21px rgb(0 0 0 / 20%),
    -5px 42px 24px rgb(0 0 0 / 16%);
}

// The ring is drawn once its pictures are in the browser, which is a moment after the card on a
// page that arrives with one. It comes up rather than appearing — and quickly, because a cover at
// half opacity has the card's own photograph showing through it and reads as a different picture.
.explore-tile__ring,
.explore-tile__ring-fade {
  animation: explore-ring-appear 280ms ease-out both;
}

@keyframes explore-ring-appear {
  from { opacity: 0; }
  to { opacity: 1; }
}

.explore-tile__ring-fade {
  position: absolute;
  left: 0;
  right: 0;
  // A short strip along the bottom rather than half the card, since the wide ring sits high in the
  // frame. Anchored to the bottom and given a height rather than a `top`: the design's 333px is
  // measured from a 400px card, and on the 320px one the bento gives a row with a single tile that
  // same offset leaves the band zero pixels tall, with nothing to bury the lower arc.
  bottom: 0;
  height: 67px;
  z-index: 0;
  pointer-events: none;
  background-image: linear-gradient(
    to top,
    var(--today-surface-dark) 0%,
    color-mix(in srgb, var(--today-surface-dark) 0%, transparent) 100%
  );
}

// The picture behind the copy (IPD-3130). It is one shipped file rather than anything the CMS
// chooses: this tile takes no payload at all, so there is nothing for a photograph to follow.
//
// The file is ALREADY blurred and already small — 1200x675, 14KB. Blurring it here instead, with
// `filter`, would mean shipping the sharp original and then asking every paint to blur a
// full-bleed layer, and on this card the sharp version is never wanted at any width.
//
// `cover` with a centred position is what holds the crop steady across the breakpoints the AC
// asks about: this tile is 2.8:1 at its widest and close to square at its narrowest, so the
// picture is cropped, not squashed, and it keeps its middle either way.
//
// The gradient is the "faded" half, and it fades ACROSS rather than uniformly: measured off the
// design, the picture is entirely absent behind the copy and reaches roughly a quarter of its
// strength at the right edge, which is what keeps the fixed light copy on a surface it can hold
// on to. Kept as a mask over the whole layer rather than as a scrim painted on top of it, so the
// tile's own dark surface is what shows through and the two cannot drift apart.
//
// 22% and not the design's own strength, which measures about 26 at the right edge. The body line
// is `--today-ink-on-dark` at 82%, and over the brightest part of the picture — the pepper, which
// the crop puts at the right of a wide tile — 26 leaves it at 3.96:1 against the 4.5:1 a 16px line
// needs. 22 reads 4.79:1 at the worst pixel of the card at either shape. The design's own mock
// never showed this because its copy wraps short of that corner; a longer translation does not,
// and German is longer. Same call as the tasks tile's body, which was raised from the design's 66%
// for the same reason.
//
// Prefixed as well, like the repo's other mask call sites: the build carries no autoprefixer and
// the unprefixed longhands are recent. Where they are dropped the mask is simply absent and the
// picture reads at a flat 22% across the whole card, copy included — quieter than the design but
// not broken.
.explore-tile__art {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.22;
  background-position: center;
  background-size: cover;
  mask-image: linear-gradient(to right, transparent 25%, #000 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 25%, #000 100%);
}

// Everything above the picture, which is the only positioned child behind them. The body copy is
// not listed because it is inside the intro block, which carries the stacking context for both.
.explore-tile__intro,
.explore-tile .explore-tile__cta {
  position: relative;
  z-index: 1;
}

.explore-tile__intro {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
}

.explore-tile__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-eyebrow);
}

.explore-tile__heading {
  @include font-today-tile-title;

  margin: 0;
}

// The design quietens this line to 82% of the card's ink rather than reaching for a second grey,
// which on a fixed dark surface is safe: 9.4:1, well clear of the 4.5:1 the size needs. Kept as
// the design's own value for that reason — unlike the promo tiles, where 66% had to be raised.
.explore-tile__body {
  @include font-body;

  font-size: var(--today-text-body);
  margin-top: var(--Spacing-spacing-3);
  margin-bottom: 0;
  color: color-mix(in srgb, var(--today-ink-on-dark) 82%, transparent);
}

// Pinned to the bottom so the card keeps its shape whatever the copy above does, exactly as the
// tasks tile pins its own. `light-white` is the atom's scheme-pinned pair — light fill, dark ink,
// in both themes — which is what this fixed dark surface needs; a theme-aware theme would turn
// the button dark-on-dark the moment the page flips.
//
// Only the box is overridden: shape, colour, hover and focus stay the atom's. The design draws a
// 48px pill that hugs its label with 32px of side padding and no vertical padding at all, while
// the atom's nearest preset is 16/48 on a wide window — which rendered this button 186x56 against
// the design's 144x48. The 48px floor replaces the atom's own 44px one, so it still clears the
// WCAG 2.2 AA target size rather than dropping under it.
// Amber for the personalised state, as the design draws it. Through the atom's `inverse` theme and
// the two variables it publishes for exactly this — a caller whose surface does not follow the
// scheme — rather than by overriding its background, which would win only on the order its chunk
// happens to be linked in. The theme matters: those variables are read by `inverse` alone, and set
// under `light-white` they do nothing at all.
//
// `--Primary-primary-color-4` is the same #EFB43F in both schemes, which the Today tokens already
// rely on for the homework tint, and the ink is the one those amber cards pair with it: 8:1.
.explore-tile .explore-tile__cta--new {
  --balm-btn-inverse-surface: var(--Primary-primary-color-4);
  --balm-btn-inverse-ink: var(--today-card-ink-on-amber);
}

.explore-tile .explore-tile__cta {
  @include font-body-sm-bold;

  font-size: var(--today-text-cta);
  align-self: flex-start;
  min-height: 48px;
  margin-top: auto;
  padding: 0 var(--Spacing-spacing-5);
}

// Last in the file so it is the last word: every rule above is a single class carrying the same
// scoped attribute, and a media query adds no specificity of its own.
@media (prefers-reduced-motion: reduce) {
  .explore-tile__ring,
  .explore-tile__ring-fade,
  .explore-tile__ring-spin,
  .explore-tile__ring-tile--arriving {
    animation: none;
  }
}
</style>
